import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useInRouterContext, NavigateFunction } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

/**
 * Existe uma entrada anterior DENTRO do app? `idx` é gravado pelo react-router em history.state.
 * (`window.history.length` não serve: conta páginas de outros sites e entradas "para frente".)
 */
export const canGoBack = (): boolean => {
  if (typeof window === 'undefined') return false;
  const idx = (window.history.state as { idx?: number } | null)?.idx;
  return typeof idx === 'number' && idx > 0;
};

/**
 * Voltar padrão: recua no histórico; sem histórico, troca (replace) pela rota de reserva.
 * Nunca empurrar a rota "pai" com navigate('/x'): isso criava loops do tipo /help <-> /settings.
 */
export const goBackOr = (navigate: NavigateFunction, fallback: string = '/') => {
  if (canGoBack()) navigate(-1);
  else navigate(fallback, { replace: true });
};

interface BackButtonProps {
  onClick?: () => void;
  to?: string;
  className?: string;
  fallbackTo?: string;
  /** Alias antigo de `fallbackTo`. */
  fallbackUrl?: string;
  id?: string;
}

/**
 * Botão Voltar padrão de todo o app (design minimal v1):
 * círculo discreto na superfície, seta que recua ao passar o mouse, mesmo visual no claro e no escuro.
 */
export const AppBackButton: React.FC<BackButtonProps> = (props) => {
  // Fora de um <Router> (ex.: player em portal/provider), useNavigate lança erro e derruba a tela.
  const inRouter = useInRouterContext();
  return inRouter ? <RoutedBackButton {...props} /> : <BackButtonView {...props} navigate={null} />;
};

const RoutedBackButton: React.FC<BackButtonProps> = (props) => {
  const navigate = useNavigate();
  return <BackButtonView {...props} navigate={navigate} />;
};

const BackButtonView: React.FC<BackButtonProps & { navigate: NavigateFunction | null }> = ({
  onClick,
  to,
  className = '',
  fallbackTo,
  fallbackUrl,
  id,
  navigate
}) => {
  const { t } = useTranslation();

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick();
      return;
    }
    const fallback = to || fallbackTo || fallbackUrl || '/';
    if (navigate) goBackOr(navigate, fallback);
    else if (canGoBack()) window.history.back();
    else window.location.assign(fallback);
  };

  return (
    <button
      type="button"
      id={id}
      onClick={handleClick}
      aria-label={t('common.back', 'Voltar')}
      className={`group inline-flex items-center justify-center w-10 h-10 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:bg-tint-stone transition-colors duration-300 cursor-pointer relative z-30 shrink-0 ${className}`}
    >
      <ArrowLeft size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
    </button>
  );
};

export default AppBackButton;
